import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ROUTE_MESSENGER_SUPPORT } from 'navigation/config/routePaths';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { useConversations } from 'providers/ConversationProvider';
import { useMessages } from 'providers/MessageProvider';
import { generateTmpMessageId } from 'providers/utils/conversationProviderUtils';
import { ConversationDto, ConversationWithLastMessageDto } from 'services/message/messageService.dto';

import { MessengerState } from './messengerState';
import {
  findCampaignConversation,
  findItemConversation,
  findSupportConversation,
  isDraftConversation,
} from './pendingConversation';
import { usePendingConversation } from './usePendingConversation';

export const useActiveConversation = () => {
  const { conversationId, userId, itemId, campaignId } = useParams();
  const { state, pathname }: { state: MessengerState; pathname: string } = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const [initialConversationId, setInitialConversationId] = useState<number>();
  const [initialConversationResolved, setInitialConversationResolved] = useState(false);

  const [selectedConversation, setSelectedConversation] = useState<ConversationDto>();
  const [currentConversations, setCurrentConversations] = useState<ConversationWithLastMessageDto[]>([]);
  const supportPrefillStarted = useRef(false);

  const {
    pendingConversation,
    draftConversation,
    startPendingConversation,
    clearPendingConversation,
    createFromPendingConversation,
    clearDraft: clearPendingDraft,
  } = usePendingConversation();

  const { getConversationMessages } = useMessages();
  const {
    conversations,
    resetConversations,
    fetchConversationMessages,
    setCurrentConversationId,
    currentConversationId,
    fetchConversations,
    fetchConversation,
    addTextMessage,
  } = useConversations();

  const activeConversation = selectedConversation ?? draftConversation;

  const conversationMessages =
    !!selectedConversation && !isDraftConversation(selectedConversation.conversationId)
      ? getConversationMessages(selectedConversation.conversationId)
      : [];

  const selectConversation = (id: number) => {
    clearPendingConversation();
    const conversation = conversations.find(conv => conv.conversation.conversationId === id);
    setSelectedConversation(conversation?.conversation);
    if (pathname !== '/messenger') {
      navigate('/messenger', { replace: true });
    }
  };

  useEffect(() => {
    resetConversations();
  }, []);

  useEffect(() => {
    if (!conversations.length) {
      fetchConversations();
    }
  }, [!!conversations.length]);

  useEffect(() => {
    if (!!conversations.length && !!currentUser) {
      resolveInitialConversationId();
    }
  }, [!!conversations.length, !!currentUser]);

  useEffect(() => {
    if (!!conversations.length && initialConversationResolved) {
      processConversations();
    }
  }, [conversations, initialConversationResolved]);

  useEffect(() => {
    if (!!selectedConversation && !isDraftConversation(selectedConversation.conversationId)) {
      fetchConversationMessages(selectedConversation.conversationId);
      setCurrentConversationId(selectedConversation.conversationId);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (!currentConversationId && !pendingConversation) {
      setSelectedConversation(undefined);
    }
  }, [currentConversationId, pendingConversation]);

  useEffect(() => {
    if (pendingConversation?.context !== 'SUPPORT' || !state?.message || supportPrefillStarted.current) {
      return;
    }

    supportPrefillStarted.current = true;
    // Create now so processConversations can select it and send the prefilled message
    createFromPendingConversation().then(createdConversationId => {
      if (createdConversationId) {
        setInitialConversationId(createdConversationId);
      }
    });
  }, [pendingConversation, state?.message]);

  const processConversations = async () => {
    const initialConversation = conversations.find(it => it.conversation.conversationId === initialConversationId);

    if (!!initialConversation) {
      const otherConversations = conversations.filter(it => it.conversation.conversationId !== initialConversationId);
      setCurrentConversations([initialConversation, ...otherConversations]);

      if (!selectedConversation || isDraftConversation(selectedConversation.conversationId)) {
        clearPendingConversation();
        setSelectedConversation(initialConversation.conversation);

        if (initialConversation.conversation.context === 'SUPPORT') {
          addInitialMessage(initialConversation.conversation.conversationId);
        }
      }
    } else {
      setCurrentConversations(conversations);
    }
  };

  const resolveInitialConversationId = async () => {
    if (!initialConversationResolved) {
      const resolvedConversationId = await findInitialConversationId();
      setInitialConversationId(resolvedConversationId);
      setInitialConversationResolved(true);
      if (resolvedConversationId) {
        navigate('/messenger', { replace: true, state });
      }
    }
  };

  const findOrStartSupportConversation = (): number | undefined => {
    const conversation = findSupportConversation(conversations);
    if (conversation) {
      return conversation.conversation.conversationId;
    }
    startPendingConversation({ context: 'SUPPORT' });
    return;
  };

  const findInitialConversationId = async (): Promise<number | undefined> => {
    if (Number(userId) === currentUser?.id) {
      // Legacy "Contact us" links used the recipient's own user id — open support instead
      return findOrStartSupportConversation();
    }

    if (!!userId && !!itemId) {
      const conversation = findItemConversation(conversations, Number(userId), Number(itemId));
      if (conversation) {
        return conversation.conversation.conversationId;
      }
      startPendingConversation({ context: 'ITEM', interlocutorId: Number(userId), objectId: Number(itemId) });
      return;
    }

    if (!!userId && !!campaignId) {
      const conversation = findCampaignConversation(conversations, Number(userId), Number(campaignId));
      if (conversation) {
        return conversation.conversation.conversationId;
      }
      startPendingConversation({ context: 'CAMPAIGN', interlocutorId: Number(userId), objectId: Number(campaignId) });
      return;
    }

    if (!!userId) {
      const conversationWithUser = conversations
        .filter(({ conversation }) =>
          conversation.interlocutors.some(interlocutor => interlocutor.id === Number(userId))
        )
        .sort((a, b) => {
          const aDate = a.lastMessage?.lastMessageCreated || '';
          const bDate = b.lastMessage?.lastMessageCreated || '';
          return bDate.localeCompare(aDate);
        })[0];

      return conversationWithUser?.conversation.conversationId;
    }

    if (pathname === ROUTE_MESSENGER_SUPPORT) {
      return findOrStartSupportConversation();
    }

    if (!!conversationId) {
      await fetchConversation(Number(conversationId));
      return Number(conversationId);
    }
  };

  const addInitialMessage = (conversationId: number) => {
    if (!!state?.message) {
      const tmpMessageId = generateTmpMessageId();
      setTimeout(() => addTextMessage({ tmpMessageId, content: state.message.trim(), conversationId }), 500);
    }
  };

  const ensureConversationId = async (): Promise<number | undefined> => {
    if (selectedConversation && !isDraftConversation(selectedConversation.conversationId)) {
      return selectedConversation.conversationId;
    }

    const createdConversationId = await createFromPendingConversation();
    if (createdConversationId) {
      setInitialConversationId(createdConversationId);
    }
    return createdConversationId;
  };

  const clearDraft = () => {
    clearPendingDraft();
    setSelectedConversation(undefined);
  };

  const closeConversation = () => {
    if (pendingConversation) {
      clearDraft();
      return;
    }
    setCurrentConversationId(undefined);
  };

  return {
    selectConversation,
    conversationMessages,
    activeConversation,
    currentConversations,
    ensureConversationId,
    closeConversation,
  };
};
